import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'src/components';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { useAlerts } from 'src/common/AlertManager';
import { CaretDown, CaretRight, Faders, SlidersHorizontal } from 'src/common/icons';
import {
  photoEditConfirm,
  photoEditDiscard,
  photoEditDetect,
  photoEditFeedback,
  PhotoEditPreviewData,
} from 'src/api/functions/assetsApi';
import { useAiRatesQuery } from 'src/api/queries/subscriptionQueries';
import { getThumbnailUrl, addCacheBuster } from 'src/utils/previews';

import { SequenceBuilder, SequenceStepUI, makePresetSteps, serializeSteps } from './SequenceBuilder';
import { CompareSlider } from './CompareSlider';
import { SaveResultForm, NameProblem, validateResultName } from './SaveResultForm';
import { usePhotoEditPreview } from './usePhotoEditPreview';
import messages from '../messages';
import styles from './AIImageEditDialog.module.scss';

type Disposition = 'conversion' | 'new_asset';

type AIImageEditDialogProps = {
  assetId: string;
  assetName: string;
  // Source pixel dimensions (from asset metadata), used to gate the
  // deterministic Enhance ops which require a >= 512px input.
  assetWidth?: number;
  assetHeight?: number;
  onClose?: () => void;
  // Fired after a preview is confirmed (persisted). `disposition` tells the
  // caller whether a new asset or a conversion was created so it can refresh
  // the right caches and leave edit mode.
  onConfirmed: (disposition: Disposition, conversionName?: string) => void;
};

// Fal's post-processing models (used by Auto Enhance + the discrete enhance
// steps) reject inputs below this on either edge. AI Restore (generative)
// has no such floor, so it stays available for small images.
const ENHANCE_MIN_EDGE = 512;

// Renders inline inside the asset details modal (same pattern as the Pintura
// editor) — NOT as an overlay dialog. The host modal supplies the header /
// back button; this component owns the editing surface only.
export const AIImageEditDialog: React.FC<AIImageEditDialogProps> = ({
  assetId,
  assetName,
  assetWidth,
  assetHeight,
  onConfirmed,
}) => {
  const { formatMessage } = useIntl();
  const accessToken = useAccessToken();
  const { showSuccess, showAlert, showInfo } = useAlerts();

  // The deterministic Enhance ops need a >= 512px source. Disable them when
  // the image is too small OR its dimensions are unknown (we can't tell);
  // AI Restore stays available either way.
  const enhanceDisabled =
    !assetWidth || !assetHeight || Math.min(assetWidth, assetHeight) < ENHANCE_MIN_EDGE;

  const [originalUrl, setOriginalUrl] = useState('');
  // Three one-click modes:
  //  - 'auto'      -> deterministic, non-destructive Auto Enhance (recommended)
  //  - 'restore'   -> generative AI Restore (photo-restoration; alters image)
  //  - 'customize' -> the editable multi-step pipeline (backend `sequence` op)
  // Apply sends `auto_enhance`, `ai_restore`, or `sequence` accordingly.
  // Default to AI Restore when Enhance is unavailable for this image.
  //
  // Cut-out work is deliberately absent: "Remove object" moved to its own
  // task-named panel (CutoutDialog), which routes a plain cut-out to the
  // 2-credit matte instead of this 8-credit generative op. Leaving a second
  // entry point here would have meant two ways to do the same job at four
  // times the price.
  const [mode, setMode] = useState<'auto' | 'restore' | 'customize'>(
    enhanceDisabled ? 'restore' : 'auto',
  );
  const customizing = mode === 'customize';
  const [steps, setSteps] = useState<SequenceStepUI[]>([]);

  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState<PhotoEditPreviewData | null>(null);

  const [confirmMode, setConfirmMode] = useState<Disposition | null>(null);
  const [conversionName, setConversionName] = useState('');
  const [newAssetName, setNewAssetName] = useState('');
  const [nameProblem, setNameProblem] = useState<NameProblem | null>(null);
  const [confirming, setConfirming] = useState(false);
  // A new asset normally wants the source's tags and fields. (Its original is
  // always private — the server enforces that, see _register_new_asset.)
  const [inheritMetadata, setInheritMetadata] = useState(true);

  /*
   * What each option costs, read live from `ai_credit_rates` via
   * /billing/ai-rates rather than hardcoded: those rates are
   * sysadmin-editable, so a baked-in number would quietly start lying after a
   * re-price.
   *
   * The keys are the operation codes this panel submits, and the server bills
   * `<op>` on apply plus `<op>_preview` for each preview
   * (`registry.resolve_rate_key` / `resolve_preview_rate_key`). When the rate
   * card has not loaded — or an op has no row — the chip is omitted rather
   * than guessed, and the action bar falls back to the vaguer "Uses AI
   * credits" it showed before.
   */
  const { data: aiRates } = useAiRatesQuery();
  const creditsFor = (op: string): number | undefined => aiRates?.flat?.[op];
  /**
   * The op the Apply button will submit — the same branch `handleApply` takes,
   * so the price shown is the price charged.
   */
  const activeOperation = (): string => {
    if (customizing) return 'sequence';
    return mode === 'restore' ? 'ai_restore' : 'auto_enhance';
  };
  /** Apply + per-preview cost for the selected mode, or the old vague line. */
  const renderCostLine = () => {
    const op = activeOperation();
    const apply = creditsFor(op);
    const preview = aiRates?.flat?.[`${op}_preview`];
    if (apply === undefined || preview === undefined) {
      return formatMessage(messages.aiEditUsesCredits);
    }
    return formatMessage(messages.aiEditCostApplyPreview, { apply, preview });
  };
  const renderCost = (op: string) => {
    const credits = creditsFor(op);
    if (credits === undefined) return null;
    return (
      <span className={styles.optionCost} data-test={`ai-edit-cost-${op}`}>
        {formatMessage(messages.aiEditCredits, { credits })}
      </span>
    );
  };

  // Post-apply explicit thumbs step: after a successful confirm we briefly keep
  // the dialog open to collect an optional 👍/👎 on the result. The apply is
  // already persisted; finalize (parent refresh + close) runs once the user
  // rates or skips (guarded so it fires exactly once, incl. on unmount).
  const [awaitingFeedback, setAwaitingFeedback] = useState(false);
  const feedbackTokenRef = useRef<string | null>(null);
  const finalizedRef = useRef(false);
  const finalizeInfoRef = useRef<{ disposition: Disposition; conversionName?: string } | null>(null);
  const onConfirmedRef = useRef(onConfirmed);
  onConfirmedRef.current = onConfirmed;

  const cancelledRef = useRef(false);
  // Track the latest staged token + whether it was confirmed so we can
  // best-effort discard it when this pane unmounts (e.g. the user clicks
  // the modal's Back button without confirming).
  const stagedTokenRef = useRef<string | null>(null);
  const confirmedRef = useRef(false);
  const assetIdRef = useRef(assetId);
  const accessTokenRef = useRef(accessToken);
  assetIdRef.current = assetId;
  accessTokenRef.current = accessToken;

  useEffect(() => {
    cancelledRef.current = false;
    // The "before" image is display-only (before/after compare + initial
    // preview). Use the optimized display image (ODI) from the CDN like the
    // rest of the UI rather than downloading the full-size original via
    // get_link — the backend operates on the real original regardless.
    const odiUrl = addCacheBuster(
      getThumbnailUrl({ width: 1600, height: 1200, id: assetId, jwt: accessToken }),
    );
    setOriginalUrl(odiUrl);
    return () => {
      cancelledRef.current = true;
      if (stagedTokenRef.current && !confirmedRef.current) {
        photoEditDiscard(assetIdRef.current, stagedTokenRef.current, accessTokenRef.current).catch(() => {});
      }
      // Applied but the user closed before rating/skipping — still finalize so
      // the parent refreshes (relationships / gallery / credits).
      if (confirmedRef.current && !finalizedRef.current && finalizeInfoRef.current) {
        finalizedRef.current = true;
        const info = finalizeInfoRef.current;
        onConfirmedRef.current(info.disposition, info.conversionName);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId]);

  // Switching to the multi-step builder for the first time seeds the
  // auto_enhance preset so the pipeline is never empty.
  const handleOpenCustomize = () => {
    setMode('customize');
    if (steps.length === 0) setSteps(makePresetSteps());
  };

  // Collapse back to the one-click mode and discard any customization
  // (Auto Enhance when available, otherwise AI Restore).
  const handleResetToAuto = () => {
    setMode(enhanceDisabled ? 'restore' : 'auto');
    setSteps(makePresetSteps());
  };

  // The backend requires at least one enabled step in a sequence.
  const enabledStepCount = steps.filter((s) => s.enabled).length;
  const applyDisabled = applying || (customizing && enabledStepCount === 0);
  const defaultResultName = customizing
    ? 'edit'
    : mode === 'restore'
      ? 'ai_restore'
      : 'auto_enhance';

  const { submitPreview } = usePhotoEditPreview({ assetId, cancelledRef });

  const setStagedPreview = (data: PhotoEditPreviewData | null) => {
    setPreview(data);
    stagedTokenRef.current = data ? data.preview_token : null;
  };

  const handleApply = async () => {
    if (applyDisabled) return;
    // Discard any prior unconfirmed preview before staging a new one.
    if (stagedTokenRef.current) {
      photoEditDiscard(assetId, stagedTokenRef.current, accessToken).catch(() => {});
      setStagedPreview(null);
    }
    setApplying(true);
    setNameProblem(null);
    setConfirmMode(null);
    try {
      let previewBody: { operation: string; params: { [key: string]: any } };
      if (customizing) {
        previewBody = { operation: 'sequence', params: { steps: serializeSteps(steps) } };
      } else {
        previewBody = { operation: mode === 'restore' ? 'ai_restore' : 'auto_enhance', params: {} };
      }
      // submitPreview owns the inline/job/error three-way and has already
      // alerted the user on every failure path, so null just means stop.
      const data = await submitPreview(previewBody);
      if (cancelledRef.current || !data) return;
      setStagedPreview(data);
    } catch (e: any) {
      if (!cancelledRef.current) showAlert(e?.message || formatMessage(messages.aiEditFailed));
    } finally {
      if (!cancelledRef.current) setApplying(false);
    }
  };

  // Thin wrapper over the shared validator: it reports which rule broke, the
  // panel decides how to show it.
  const validateName = (name: string, allowExtension = false): boolean => {
    const problem = validateResultName(name, allowExtension);
    setNameProblem(problem);
    return problem === null;
  };

  const handleConfirm = async () => {
    if (!preview || confirming || !confirmMode) return;

    let params: { [key: string]: any } = {};
    if (confirmMode === 'conversion') {
      if (!validateName(conversionName)) return;
      params = { conversion_name: conversionName };
    } else if (newAssetName) {
      // optional filename for a new asset
      if (!validateName(newAssetName, true)) return;
      params = { filename: newAssetName, inherit_metadata: inheritMetadata };
    } else {
      params = { inherit_metadata: inheritMetadata };
    }

    setConfirming(true);
    try {
      const result = await photoEditConfirm(
        assetId,
        { preview_token: preview.preview_token, disposition: confirmMode, params },
        accessToken,
      );
      if (result.kind === 'error') {
        throw new Error(
          result.error?.message || result.error?.error?.message || formatMessage(messages.aiEditFailed),
        );
      }
      // Mark confirmed so the unmount cleanup doesn't discard it.
      confirmedRef.current = true;
      stagedTokenRef.current = null;
      if (result.kind === 'job') {
        // Large images apply asynchronously in the background; the
        // conversion / new asset only appears once that job completes.
        showInfo(
          confirmMode === 'conversion'
            ? formatMessage(messages.aiEditProcessingConversion, { conversionName })
            : formatMessage(messages.aiEditProcessingNewAsset),
        );
      } else {
        showSuccess(
          confirmMode === 'conversion'
            ? formatMessage(messages.aiEditSavedConversion, { conversionName })
            : formatMessage(messages.aiEditSavedNewAsset),
        );
      }
      const token = result.feedbackToken;
      finalizeInfoRef.current = {
        disposition: confirmMode,
        conversionName: confirmMode === 'conversion' ? conversionName : undefined,
      };
      if (token) {
        // Offer an optional thumbs verdict before closing.
        feedbackTokenRef.current = token;
        setAwaitingFeedback(true);
      } else {
        finalizeConfirm();
      }
    } catch (e: any) {
      showAlert(e?.message || formatMessage(messages.aiEditFailed));
    } finally {
      setConfirming(false);
    }
  };

  // Refresh the parent + close, exactly once (also runs on unmount if the user
  // closes during the thumbs step — see the cleanup effect).
  const finalizeConfirm = () => {
    if (finalizedRef.current) return;
    finalizedRef.current = true;
    const info = finalizeInfoRef.current;
    if (info) onConfirmed(info.disposition, info.conversionName);
  };

  const sendVerdict = (verdict: 'up' | 'down') => {
    const token = feedbackTokenRef.current;
    if (token) {
      // Fire-and-forget: never block closing on the feedback write.
      photoEditFeedback(assetId, { feedback_token: token, verdict }, accessToken).catch(() => {});
    }
    finalizeConfirm();
  };

  const handleStartOver = () => {
    if (stagedTokenRef.current) {
      photoEditDiscard(assetId, stagedTokenRef.current, accessToken).catch(() => {});
    }
    setStagedPreview(null);
    setConfirmMode(null);
    setNameProblem(null);
    setConversionName('');
    setNewAssetName('');
  };

  const renderActionBar = () => {
    // Post-apply: optional thumbs verdict on the saved result.
    if (awaitingFeedback) {
      return (
        <>
          <span className={styles.actionContext}>{formatMessage(messages.aiEditFeedbackPrompt)}</span>
          <div className={styles.actionButtons}>
            <Button mode="flat" size="small" onClick={() => sendVerdict('up')}>
              {formatMessage(messages.aiEditFeedbackGood)}
            </Button>
            <Button mode="flat" size="small" onClick={() => sendVerdict('down')}>
              {formatMessage(messages.aiEditFeedbackBad)}
            </Button>
            <Button mode="primary" size="small" onClick={finalizeConfirm}>
              {formatMessage(messages.aiEditFeedbackSkip)}
            </Button>
          </div>
        </>
      );
    }

    // Result phase: a preview is staged and waiting to be saved or discarded.
    if (preview) {
      if (confirmMode === 'conversion' || confirmMode === 'new_asset') {
        const isConversion = confirmMode === 'conversion';
        return (
          <SaveResultForm
            disposition={confirmMode}
            value={isConversion ? conversionName : newAssetName}
            onChange={(value: string) => (isConversion ? setConversionName(value) : setNewAssetName(value))}
            onBack={() => setConfirmMode(null)}
            onConfirm={handleConfirm}
            problem={nameProblem}
            confirming={confirming}
            inheritMetadata={inheritMetadata}
            onInheritMetadataChange={setInheritMetadata}
          />
        );
      }

      return (
        <>
          <span className={styles.actionContext}>{formatMessage(messages.aiEditSaveResult)}</span>
          <div className={styles.actionButtons}>
            <Button mode="flat" size="small" onClick={handleApply} loading={applying} disabled={applyDisabled}>
              {formatMessage(messages.aiEditReapply)}
            </Button>
            <Button mode="flat" size="small" onClick={handleStartOver}>
              {formatMessage(messages.aiEditStartOver)}
            </Button>
            <Button
              mode="primary"
              size="small"
              onClick={() => {
                setNewAssetName(defaultResultName);
                setNameProblem(null);
                setConfirmMode('new_asset');
              }}
            >
              {formatMessage(messages.aiEditAddNewAsset)}
            </Button>
            <Button
              mode="primary"
              size="small"
              onClick={() => {
                setConversionName(defaultResultName);
                setNameProblem(null);
                setConfirmMode('conversion');
              }}
            >
              {formatMessage(messages.aiEditAddConversion)}
            </Button>
          </div>
        </>
      );
    }

    // Configure phase: choose Auto Enhance or a custom pipeline, then apply.
    return (
      <>
        <span className={styles.actionContext}>
          {customizing
              ? formatMessage(messages.aiEditCustomize)
              : mode === 'restore'
                ? formatMessage(messages.aiEditAiRestore)
                : formatMessage(messages.aiEditAutoEnhance)}
          <span className={styles.actionContextDot}> · </span>
          {renderCostLine()}
        </span>
        <Button mode="primary" size="small" onClick={handleApply} loading={applying} disabled={applyDisabled}>
          {formatMessage(messages.aiEditApply)}
        </Button>
      </>
    );
  };

  return (
    <div className={styles.panel}>
      <div className={styles.body}>
        <CompareSlider
          originalUrl={originalUrl}
          resultUrl={preview?.preview_url}
          alt={assetName}
          busy={applying}
          busyLabel={formatMessage(messages.aiEditApplying)}
          beforeLabel={formatMessage(messages.aiEditBefore)}
          afterLabel={formatMessage(messages.aiEditAfter)}
        />

        {/* Accordion: only the selected mode shows its description/body; the
            rest collapse to a compact title row to free up space. */}
        <div className={styles.controls}>
          {/* Auto Enhance */}
          <div className={mode === 'auto' ? `${styles.customize} ${styles.customizeOpen}` : styles.customize}>
            <button
              type="button"
              className={styles.customizeHeader}
              onClick={() => setMode('auto')}
              disabled={applying || enhanceDisabled}
              aria-expanded={mode === 'auto'}
            >
              <span className={styles.optionIcon} aria-hidden="true">
                <Faders />
              </span>
              <span className={styles.optionText}>
                <span className={styles.optionTitleRow}>
                  <span className={styles.optionTitle}>{formatMessage(messages.aiEditAutoEnhance)}</span>
                  {!enhanceDisabled && (
                    <span className={styles.optionBadge}>{formatMessage(messages.aiEditRecommended)}</span>
                  )}
                  {renderCost('auto_enhance')}
                </span>
                {(mode === 'auto' || enhanceDisabled) && (
                  <span className={styles.optionDesc}>
                    {enhanceDisabled
                      ? formatMessage(messages.aiEditEnhanceTooSmall)
                      : formatMessage(messages.aiEditAutoEnhanceDesc)}
                  </span>
                )}
              </span>
              <span className={styles.customizeCaret} aria-hidden="true">
                {mode === 'auto' ? <CaretDown /> : <CaretRight />}
              </span>
            </button>
          </div>

          {/* AI Restore */}
          <div className={mode === 'restore' ? `${styles.customize} ${styles.customizeOpen}` : styles.customize}>
            <button
              type="button"
              className={styles.customizeHeader}
              onClick={() => setMode('restore')}
              disabled={applying}
              aria-expanded={mode === 'restore'}
            >
              <span className={styles.optionIcon} aria-hidden="true">
                <Faders />
              </span>
              <span className={styles.optionText}>
                <span className={styles.optionTitleRow}>
                  <span className={styles.optionTitle}>{formatMessage(messages.aiEditAiRestore)}</span>
                  <span className={styles.optionBadge}>{formatMessage(messages.aiEditGenerative)}</span>
                  {renderCost('ai_restore')}
                </span>
                {mode === 'restore' && (
                  <span className={styles.optionDesc}>{formatMessage(messages.aiEditAiRestoreDesc)}</span>
                )}
              </span>
              <span className={styles.customizeCaret} aria-hidden="true">
                {mode === 'restore' ? <CaretDown /> : <CaretRight />}
              </span>
            </button>
          </div>

          {/* Custom enhancement */}
          <div
            className={
              customizing
                ? `${styles.customize} ${styles.customizeOpen} ${styles.customizeGrow}`
                : styles.customize
            }
          >
            <button
              type="button"
              className={styles.customizeHeader}
              onClick={() => (customizing ? undefined : handleOpenCustomize())}
              disabled={applying}
              aria-expanded={customizing}
            >
              <span className={styles.optionIcon} aria-hidden="true">
                <SlidersHorizontal />
              </span>
              <span className={styles.optionText}>
                <span className={styles.optionTitleRow}>
                  <span className={styles.optionTitle}>{formatMessage(messages.aiEditCustomize)}</span>
                  {/* A custom pipeline runs as one `sequence` op, so it bills
                      once regardless of how many steps it carries. */}
                  {renderCost('sequence')}
                </span>
                {customizing && (
                  <span className={styles.optionDesc}>{formatMessage(messages.aiEditCustomizeHint)}</span>
                )}
              </span>
              <span className={styles.customizeCaret} aria-hidden="true">
                {customizing ? <CaretDown /> : <CaretRight />}
              </span>
            </button>

            {customizing && (
              <div className={styles.customizeBody}>
                <span className={styles.hint}>{formatMessage(messages.aiEditDragHint)}</span>
                <SequenceBuilder steps={steps} onChange={setSteps} disabled={applying} />
                <button type="button" className={styles.resetLink} onClick={handleResetToAuto} disabled={applying}>
                  {formatMessage(messages.aiEditResetAuto)}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.actionBar}>{renderActionBar()}</div>
    </div>
  );
};

export default AIImageEditDialog;
